import { useEffect, useState } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { peekStreakState, useCurrentUserProfile } from '@busy/api';
import { AppShell } from './components/AppShell';
import { AuthGate } from './routes/AuthGate';
import { Landing } from './routes/Landing';
import { Onboarding } from './routes/Onboarding';
import { Today } from './routes/Today';
import { Dashboard } from './routes/Dashboard';
import { Lessons } from './routes/Lessons';
import { Clients } from './routes/Clients';
import { Settings } from './routes/Settings';
import { Achievements } from './routes/Achievements';
import { CutLog } from './routes/CutLog';
import { SignIn } from './routes/SignIn';
import { SignUp } from './routes/SignUp';
import { useUI } from './store/ui';
import { useAuth } from './store/auth';
import { SplashScreen } from './components/SplashScreen';
import { WhatsNewModal } from './components/WhatsNewModal';
import { RescueStreakModal } from './components/RescueStreakModal';
import { StreakProtectionCelebrationModal } from './components/StreakProtectionCelebrationModal';
import { getRecentChanges } from './lib/changelog';
import { useStreakCelebrationQueue } from './lib/streakCelebrations';

// Splash gating: track which calendar day the daily quote splash last ran on
// this device. Once-a-day frequency keeps the moment feeling like a ritual
// rather than a chore — the user complained when it fired on every page reload
// (Supabase fires SIGNED_IN on token-refresh too, so the auth-event-based gate
// kept tripping). YYYY-MM-DD in local time, stored in localStorage.
const SPLASH_LAST_SHOWN_KEY = 'busy.splash.lastShownYmd';
// What's New uses a different gate: the id of the newest changelog entry the
// user has acknowledged. Show the modal whenever the latest shipped entry's
// id differs from this. This fires on first launch of each new release —
// even mid-day deploys — but doesn't repeat once the user has seen the
// current set. Avoids both "show every reload" and "miss mid-day updates."
const WHATSNEW_LAST_SEEN_ENTRY_KEY = 'busy.whatsnew.lastSeenEntryId';

function todayYmd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function lsGet(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function lsSet(key: string, value: string) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore — quota / private mode; popup will simply re-show */
  }
}

export default function App() {
  const theme = useUI((s) => s.theme);
  const language = useUI((s) => s.language);
  const setLanguage = useUI((s) => s.setLanguage);
  const initAuth = useAuth((s) => s.init);
  const clearJustSignedIn = useAuth((s) => s.clearJustSignedIn);
  const session = useAuth((s) => s.session);
  const profile = useCurrentUserProfile();
  // Set once on mount: did this device already see the splash today? Reading
  // localStorage in render would be fine but a one-shot state lets us flip it
  // optimistically on dismiss without blocking on a re-read.
  const [splashShownToday, setSplashShownToday] = useState<boolean>(() => {
    return lsGet(SPLASH_LAST_SHOWN_KEY) === todayYmd();
  });
  // Recent changelog entries — only matters when there's at least one within
  // the lookback window. Computed once on mount so the popup doesn't churn
  // if the user keeps the tab open across midnight.
  const [recentChanges] = useState(() => getRecentChanges());
  const latestEntryId = recentChanges[0]?.id ?? null;
  // What's New is gated on whether the user has acknowledged the latest
  // entry, NOT on a daily flag. This way a mid-day deploy with new
  // entries triggers the popup again, but already-seen content doesn't
  // re-fire on every page reload.
  const [whatsNewSeenEntryId, setWhatsNewSeenEntryId] = useState<string | null>(
    () => lsGet(WHATSNEW_LAST_SEEN_ENTRY_KEY),
  );

  // Apply theme attribute to <html> so CSS vars flip
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Lazy break detection (migration 0078). The streak system is
  // event-driven on the DB side — bump_streak_for_user only fires from
  // visits/lessons, so a barber who opens the app without engaging
  // could sit forever on a stale current_streak + full shields +
  // intact cooloffs. peek_streak_state walks the same cooloff math
  // with no engagement assumed and writes the broken state if the
  // bank's depleted. Idempotent (no-op for same-day + broke_at-set
  // states), so calling once per session is enough — RescueStreakModal
  // reads from the streak cache that this invalidates.
  //
  // We also re-peek on visibility change so a phone left open across
  // midnight doesn't show stale state when the barber returns to it.
  useEffect(() => {
    if (!session || !profile.data?.onboarded_at) return;
    void peekStreakState();
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void peekStreakState();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [session, profile.data?.onboarded_at]);

  // Subscribe to auth state once at app boot
  useEffect(() => {
    const unsub = initAuth();
    return () => unsub?.();
  }, [initAuth]);

  // Hydrate the local UI language from the user's DB-saved preference
  // when the profile loads. Without this, a fresh-device sign-in
  // defaults to 'en' (no localStorage cache yet) even if the user
  // previously picked Spanish on another device. Runs whenever
  // profile.data.language differs from the current UI language.
  useEffect(() => {
    const dbLang = profile.data?.language;
    if (dbLang && dbLang !== language) {
      setLanguage(dbLang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.data?.language]);

  // Show the daily quote splash on the first launch of each calendar day
  // for an authenticated, post-onboarding user. The localStorage flag is
  // the source of truth — `justSignedIn` is no longer reliable on its own
  // because Supabase fires SIGNED_IN on token-refresh too, which made the
  // splash pop on every page reload.
  const authedAndOnboarded = !!session && !!profile.data?.onboarded_at;
  const showSplash = !splashShownToday && authedAndOnboarded;
  // What's New chains after the splash. Fires whenever the newest shipped
  // entry id differs from the last one this device acknowledged — covers
  // both the first launch each day and mid-day deploys.
  const showWhatsNew =
    !showSplash &&
    authedAndOnboarded &&
    latestEntryId !== null &&
    whatsNewSeenEntryId !== latestEntryId &&
    recentChanges.length > 0;

  function dismissSplash() {
    lsSet(SPLASH_LAST_SHOWN_KEY, todayYmd());
    setSplashShownToday(true);
    clearJustSignedIn();
  }

  function dismissWhatsNew() {
    if (latestEntryId) {
      lsSet(WHATSNEW_LAST_SEEN_ENTRY_KEY, latestEntryId);
      setWhatsNewSeenEntryId(latestEntryId);
    }
  }

  // Streak protection celebrations — fires for newly-earned cooloffs and
  // shields. Gated behind splash + What's New so a fresh-day login doesn't
  // stack five popups on top of each other. The hook computes its queue
  // once when ready flips true, then drains as the user taps Got it.
  const celebrationsReady = !showSplash && !showWhatsNew && authedAndOnboarded;
  const { current: streakCelebration, dismissCurrent: dismissStreakCelebration } =
    useStreakCelebrationQueue({ ready: celebrationsReady });

  return (
    <>
    {showSplash && <SplashScreen onDismiss={dismissSplash} />}
    {showWhatsNew && <WhatsNewModal entries={recentChanges} onDismiss={dismissWhatsNew} />}
    {/* Rescue prompt — fires on app open when the bump trigger detected a
        gap that exhausted the cooloff bank. Shows the broken streak length
        and the shield cost to restore; user can tap Y/N. Dismissed
        permanently once they decide either way. */}
    {authedAndOnboarded && !showSplash && !showWhatsNew && <RescueStreakModal />}
    {/* Streak protection celebration — newly-earned cooloff (snowfall),
        single earned shield (lightning), or grouped starter/anniversary
        shields (balloons). One at a time; hook drains the queue as each
        is dismissed. */}
    {celebrationsReady && streakCelebration && (
      <StreakProtectionCelebrationModal
        kind={streakCelebration.kind}
        count={streakCelebration.count}
        onDismiss={dismissStreakCelebration}
      />
    )}
    <Routes>
      {/* Public marketing surface — `/` is the landing page; auth screens stay
          public too so visitors can sign in without bouncing. Landing
          internally redirects to /today when a session exists. */}
      <Route path="/" element={<Landing />} />
      <Route path="/sign-in" element={<SignIn />} />
      <Route path="/sign-up" element={<SignUp />} />
      <Route element={<AuthGate />}>
        <Route path="/onboarding" element={<Onboarding />} />
        <Route element={<OnboardingGate />}>
          <Route element={<AppShell />}>
            <Route path="/today" element={<Today />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/lessons" element={<Lessons />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/cuts" element={<CutLog />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/achievements" element={<Achievements />} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

// Routes the user to /onboarding if their profile hasn't been onboarded yet.
// Sits between AuthGate (proves session) and AppShell (renders the post-auth
// surfaces). Profile loading is awaited so we don't briefly flash /today before
// redirecting to /onboarding.
function OnboardingGate() {
  const profile = useCurrentUserProfile();
  if (profile.loading) return null;
  if (profile.data && !profile.data.onboarded_at) {
    return <Navigate to="/onboarding" replace />;
  }
  return <Outlet />;
}
